/**
 * Invoice Builder
 * 
 * CRITICAL: CSV is SOURCE INPUT ONLY.
 * This module converts CSV rows into structured Invoice JSON.
 * UI renders from JSON, NOT directly from CSV rows.
 * 
 * Invoice is a SALES artifact, not a finance document.
 */

/**
 * Build structured invoice JSON from CSV rows
 * 
 * CSV row types:
 * - meta: invoice header info
 * - line_item: product/service sold
 * - fee: platform fees, payment processing fees
 * - shipping: shipping costs
 * - note: additional notes/narrative
 * 
 * @param {array} csvRows - Parsed CSV rows with headers
 * @param {string} transactionId - Parent transaction ID
 * @param {object} defaults - Default values (customer, platform, etc.)
 * @returns {object} - Structured invoice JSON
 */
export function buildInvoiceFromCSV(csvRows, transactionId, defaults = {}) {
  if (!csvRows || csvRows.length === 0) {
    throw new Error('CSV file is empty');
  }

  // Detect CSV format (enhanced vs simple)
  const hasRecordType = csvRows.some(row => row.record_type);
  const hasSection = csvRows.some(row => row.section);
  const hasAffectsInvoiceTotal = csvRows.some(row => row.affects_invoice_total !== undefined);
  const isEnhancedFormat = hasRecordType || hasSection || hasAffectsInvoiceTotal;

  // Extract metadata
  let invoiceId = `INV-${Date.now()}`;
  let invoiceDate = new Date().toISOString().split('T')[0];
  let orderReference = transactionId;
  let customer = defaults.customerName || 'Unknown Customer';
  let platform = defaults.originPlatform || 'Direct';
  let shippingAddress = '';
  
  // Process meta rows in enhanced format
  if (isEnhancedFormat) {
    const metaRows = csvRows.filter(row => 
      (row.record_type || row.type || '').toLowerCase() === 'meta'
    );
    
    metaRows.forEach(meta => {
      const section = (meta.section || '').toLowerCase();
      const ref = meta.reference || meta.invoice_id || meta.value;
      
      if (section === 'invoice' && ref) invoiceId = ref;
      if (section === 'transaction' && ref) orderReference = ref;
      if (section === 'platform' && ref) platform = ref;
      if (section === 'customer') {
        customer = meta.description || ref || customer;
      }
      if (section === 'shipping_address' || section === 'ship_to') {
        shippingAddress = meta.description || ref || '';
      }
      if (meta.date) invoiceDate = meta.date;
    });
  } else {
    // Legacy format - extract from first row
    const metaRow = csvRows.find(row => row.type === 'meta') || csvRows[0];
    invoiceId = metaRow.invoice_id || csvRows[0]?.invoice_id || invoiceId;
    invoiceDate = metaRow.date || invoiceDate;
    orderReference = metaRow.order_reference || metaRow.order_id || orderReference;
    customer = metaRow.customer || metaRow.customer_name || customer;
    platform = metaRow.platform || platform;
  }

  // Check for header record type
  const headerRows = csvRows.filter(row => 
    (row.record_type || '').toLowerCase() === 'header'
  );
  
  headerRows.forEach(header => {
    const field = (header.field || '').toLowerCase();
    const value = header.value || '';
    
    if (field === 'customer_name') customer = value;
    if (field === 'shipping_address') shippingAddress = value;
  });
  
  // Extract structured data
  const lineItems = [];
  const payments = [];
  const adjustments = [];
  const shippingEvents = [];
  let notes = '';
  
  csvRows.forEach(row => {
    const recordType = (row.record_type || row.type || '').toLowerCase().trim();
    const section = (row.section || '').toLowerCase().trim();
    
    // Skip meta rows
    if (recordType === 'meta') return;
    
    // Extract common fields
    const description = row.description || row.item_name || row.name || '';
    const amount = parseFloat(row.amount || row.price || row.unit_price || 0);
    const quantity = parseInt(row.qty || row.quantity || 1);
    const date = row.date || '';
    const reference = row.reference || '';
    const note = row.notes || row.note || '';
    
    // Determine visibility and invoice impact
    let visibleToCustomer = true;
    let affectsInvoiceTotal = false;
    
    if (isEnhancedFormat) {
      // Parse boolean strings from CSV
      const visibleStr = String(row.visible_to_customer || 'true').toLowerCase().trim();
      const affectsStr = String(row.affects_invoice_total || '').toLowerCase().trim();
      
      visibleToCustomer = visibleStr === 'true' || visibleStr === '1' || visibleStr === 'yes';
      
      // If affects_invoice_total is explicitly set, use it; otherwise default based on row type
      if (affectsStr !== '') {
        affectsInvoiceTotal = affectsStr === 'true' || affectsStr === '1' || affectsStr === 'yes';
      } else {
        // Default: line_items and fees affect total, payments/adjustments do not
        affectsInvoiceTotal = (recordType === 'line_item' || recordType === 'fee' || recordType === 'shipping');
      }
      
      console.log('Parsing row:', {
        recordType,
        section,
        description: description.substring(0, 30),
        visibleStr,
        affectsStr,
        visibleToCustomer,
        affectsInvoiceTotal
      });
    } else {
      // Legacy: visible_to_customer defaults to true, check for false
      visibleToCustomer = row.visible_to_customer !== 'false' && 
                          row.visible_to_customer !== '0' &&
                          row.visible_to_customer !== false;
      // Legacy: affects total if visible
      affectsInvoiceTotal = visibleToCustomer;
    }
    
    // Process by record type
    if (recordType === 'line_item' || (section === 'product' || section === 'upgrade')) {
      if (description.trim()) {
        const item = {
          description: description.trim(),
          amount: parseFloat(amount.toFixed(2)),
          quantity: quantity,
          visible_to_customer: visibleToCustomer,
          affects_invoice_total: affectsInvoiceTotal,
          reference: reference,
          date: date
        };
        console.log('Adding line item:', item);
        lineItems.push(item);
      }
    }
    else if (recordType === 'payment') {
      if (description.trim() && amount !== 0) {
        payments.push({
          description: description.trim(),
          amount: parseFloat(amount.toFixed(2)),
          date: date,
          reference: reference,
          visible_to_customer: visibleToCustomer
        });
      }
    }
    else if (recordType === 'shipping_event') {
      if (description.trim()) {
        shippingEvents.push({
          description: description.trim(),
          amount: parseFloat(amount.toFixed(2)),
          date: date,
          reference: reference,
          note: note.trim(),
          visible_to_customer: false // SCM only
        });
      }
    }
    else if (recordType === 'adjustment' || recordType === 'replacement' || recordType === 'insurance') {
      adjustments.push({
        type: recordType,
        description: description.trim(),
        amount: parseFloat(amount.toFixed(2)),
        date: date,
        reference: reference,
        note: note.trim(),
        visible_to_customer: visibleToCustomer,
        affects_invoice_total: affectsInvoiceTotal
      });
    }
    else if (recordType === 'note') {
      if (description.trim()) {
        notes += (notes ? '\n' : '') + description.trim();
      }
    }
  });

  // Extract fees (from both formats)
  const fees = [];
  csvRows.forEach(row => {
    const recordType = (row.record_type || row.type || '').toLowerCase().trim();
    const section = (row.section || '').toLowerCase().trim();
    
    if (recordType === 'fee' || section === 'platform' && row.amount) {
      const description = row.description || row.fee_type || row.fee_name || 'Fee';
      const amount = parseFloat(row.amount || row.fee_amount || 0);
      
      let visibleToCustomer = true;
      let affectsInvoiceTotal = false;
      
      if (isEnhancedFormat) {
        const visibleStr = String(row.visible_to_customer || 'true').toLowerCase().trim();
        const affectsStr = String(row.affects_invoice_total || 'false').toLowerCase().trim();
        
        visibleToCustomer = visibleStr === 'true' || visibleStr === '1' || visibleStr === 'yes';
        affectsInvoiceTotal = affectsStr === 'true' || affectsStr === '1' || affectsStr === 'yes';
      } else {
        visibleToCustomer = row.visible_to_customer !== 'false' && 
                            row.visible_to_customer !== '0' &&
                            row.visible_to_customer !== false;
        affectsInvoiceTotal = visibleToCustomer;
      }
      
      if (description.trim() && amount !== 0) {
        fees.push({
          description: description.trim(),
          amount: parseFloat(amount.toFixed(2)),
          visible_to_customer: visibleToCustomer,
          affects_invoice_total: affectsInvoiceTotal,
          reference: row.reference || '',
          date: row.date || ''
        });
      }
    }
  });

  // Extract shipping
  let shipping = {
    description: 'Shipping Cost',
    amount: 0,
    visible_to_customer: true,
    affects_invoice_total: false
  };

  const shippingRow = csvRows.find(row => {
    const recordType = (row.record_type || row.type || '').toLowerCase();
    const section = (row.section || '').toLowerCase();
    return recordType === 'shipping' || section === 'shipping' || row.shipping_amount || row.shipping_cost;
  });

  if (shippingRow) {
    let visibleToCustomer = true;
    let affectsInvoiceTotal = false;
    
    if (isEnhancedFormat) {
      const visibleStr = String(shippingRow.visible_to_customer || 'true').toLowerCase().trim();
      const affectsStr = String(shippingRow.affects_invoice_total || 'false').toLowerCase().trim();
      
      visibleToCustomer = visibleStr === 'true' || visibleStr === '1' || visibleStr === 'yes';
      affectsInvoiceTotal = affectsStr === 'true' || affectsStr === '1' || affectsStr === 'yes';
    } else {
      visibleToCustomer = shippingRow.visible_to_customer !== 'false' && 
                          shippingRow.visible_to_customer !== '0' &&
                          shippingRow.visible_to_customer !== false;
      affectsInvoiceTotal = visibleToCustomer;
    }
                              
    shipping = {
      description: shippingRow.description || 'Shipping Cost',
      amount: parseFloat(shippingRow.amount || shippingRow.shipping_amount || shippingRow.shipping_cost || 0),
      visible_to_customer: visibleToCustomer,
      affects_invoice_total: affectsInvoiceTotal,
      reference: shippingRow.reference || '',
      date: shippingRow.date || ''
    };
  }

  // Calculate totals - ONLY from items that affect invoice total
  const invoiceLineItems = lineItems.filter(item => item.affects_invoice_total);
  const invoiceFees = fees.filter(fee => fee.affects_invoice_total);
  const shippingAmount = shipping.affects_invoice_total ? shipping.amount : 0;
  
  console.log('Invoice calculation:', {
    totalLineItems: lineItems.length,
    invoiceLineItems: invoiceLineItems.length,
    invoiceLineItemsData: invoiceLineItems,
    totalFees: fees.length,
    invoiceFees: invoiceFees.length,
    shippingAmount: shippingAmount
  });
  
  const subtotal = invoiceLineItems.reduce((sum, item) => 
    sum + (item.amount * (item.quantity || 1)), 0
  );
  const totalFees = invoiceFees.reduce((sum, fee) => sum + fee.amount, 0);
  const total = subtotal + totalFees + shippingAmount;

  // Build structured invoice JSON
  return {
    invoice_id: invoiceId,
    order_id: orderReference,
    customer: customer,
    shipping_address: shippingAddress,
    platform: platform,
    date: invoiceDate,
    line_items: lineItems,
    fees: fees,
    shipping: shippingAmount,
    shipping_details: shipping,
    payments: payments,
    adjustments: adjustments,
    shipping_events: shippingEvents,
    notes: notes.trim(),
    totals: {
      subtotal: parseFloat(subtotal.toFixed(2)),
      totalFees: parseFloat(totalFees.toFixed(2)),
      shipping: parseFloat(shippingAmount.toFixed(2)),
      total: parseFloat(total.toFixed(2))
    },
    // Payment attempts are local-only runtime data; include empty array by default
    paymentAttempts: [],
    source: 'CSV Import',
    status: 'Draft',
    created_at: new Date().toISOString()
  };
}

/**
 * Validate invoice structure
 * Ensures all required fields are present
 * 
 * @param {object} invoice - Invoice to validate
 * @returns {object} - { valid: boolean, errors: string[] }
 */
export function validateInvoice(invoice) {
  const errors = [];

  if (!invoice.invoice_id) {
    errors.push('Invoice ID is required');
  }

  if (!invoice.customer) {
    errors.push('Customer name is required');
  }

  if (!invoice.date) {
    errors.push('Invoice date is required');
  }

  // CRITICAL: Must have at least one VISIBLE line item with positive value that affects invoice total
  const invoiceLineItems = (invoice.line_items || []).filter(
    item => item.affects_invoice_total && item.amount > 0
  );
  
  if (!invoiceLineItems || invoiceLineItems.length === 0) {
    errors.push('At least one line item that affects invoice total with positive amount is required');
  }

  // Validate invoice line items have description and amount
  if (invoiceLineItems) {
    invoiceLineItems.forEach((item, idx) => {
      if (!item.description) {
        errors.push(`Invoice line item ${idx + 1} missing description`);
      }
      if (typeof item.amount !== 'number') {
        errors.push(`Invoice line item ${idx + 1} missing valid amount`);
      }
    });
  }
  
  // Invoice total must be calculated from items that affect invoice total and cannot be negative
  const invoiceFees = (invoice.fees || []).filter(fee => fee.affects_invoice_total);
  const shippingAmount = invoice.shipping_details?.affects_invoice_total
    ? (invoice.shipping || 0) 
    : 0;
  
  const invoiceSubtotal = invoiceLineItems.reduce((sum, item) => 
    sum + (item.amount * (item.quantity || 1)), 0
  );
  const invoiceFeesTotal = invoiceFees.reduce((sum, fee) => sum + fee.amount, 0);
  const calculatedTotal = invoiceSubtotal + invoiceFeesTotal + shippingAmount;
  
  if (calculatedTotal < 0) {
    errors.push('Invoice total cannot be negative (customer invoice total must be >= 0)');
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Recalculate invoice totals
 * Call this after editing line items, fees, or shipping
 * 
 * @param {object} invoice - Invoice to recalculate
 * @returns {object} - Updated invoice with recalculated totals
 */
export function recalculateInvoiceTotals(invoice) {
  const lineItems = invoice.line_items || [];
  const fees = invoice.fees || [];
  const shippingAmount = typeof invoice.shipping === 'number' 
    ? invoice.shipping 
    : (invoice.shipping?.amount || 0);

  const subtotal = lineItems.reduce((sum, item) => 
    sum + (item.amount * (item.quantity || 1)), 0
  );
  const totalFees = fees.reduce((sum, fee) => sum + fee.amount, 0);
  const total = subtotal + totalFees + shippingAmount;

  return {
    ...invoice,
    totals: {
      subtotal: parseFloat(subtotal.toFixed(2)),
      totalFees: parseFloat(totalFees.toFixed(2)),
      shipping: parseFloat(shippingAmount.toFixed(2)),
      total: parseFloat(total.toFixed(2))
    }
  };
}

/**
 * Create empty invoice template
 * Use this for manual invoice creation
 * 
 * @param {object} defaults - Default values
 * @returns {object} - Empty invoice structure
 */
export function createEmptyInvoice(defaults = {}) {
  return {
    invoice_id: defaults.invoiceId || `INV-${Date.now()}`,
    order_id: defaults.orderId || defaults.transactionId || '',
    customer: defaults.customer || defaults.customerName || '',
    platform: defaults.platform || defaults.originPlatform || 'Direct',
    date: defaults.date || new Date().toISOString().split('T')[0],
    line_items: [],
    fees: [],
    shipping: 0,
    notes: '',
    totals: {
      subtotal: 0,
      totalFees: 0,
      shipping: 0,
      total: 0
    },
    // Local payment attempts for invoice (simulate only)
    paymentAttempts: [],
    source: defaults.source || 'Manual',
    status: 'Draft',
    created_at: new Date().toISOString()
  };
}
